import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTally } from "@/context/TallyContext";
import { amount, dateLabel, drcr, periodPresets, useBooks } from "@/lib/books";
import { ExportMenu, Loaded, PeriodFilter } from "@/components/books/ui";

/** The financial-year start month of the connected company (April unless Tally says otherwise). */
export function useFyMonth() {
  const { company } = useTally() || {};
  const m = Number((company?.starting_from || "").slice(5, 7));
  return m >= 1 && m <= 12 ? m : 4;
}

/**
 * A date range kept in the URL (?from=&to=) so a drill-down link, a refresh or
 * a shared link lands on the same figures. Defaults to a named preset.
 */
export function usePeriod(defaultPreset = "this_fy") {
  const fyMonth = useFyMonth();
  const [params, setParams] = useSearchParams();
  const fallback = useMemo(() => periodPresets(fyMonth).find((p) => p.key === defaultPreset), [fyMonth, defaultPreset]);
  const value = { from: params.get("from") || fallback.from, to: params.get("to") || fallback.to };
  const setValue = useCallback((v) => {
    const next = new URLSearchParams(params);
    next.set("from", v.from);
    next.set("to", v.to);
    setParams(next, { replace: true });
  }, [params, setParams]);
  return [value, setValue, fyMonth];
}

/** A ledger's statement for a period: opening, every entry with running balance, closing. */
export function LedgerStatement({ guid, period, setPeriod, fyMonth, showFilter = true }) {
  const params = { date_from: period.from, date_to: period.to };
  const state = useBooks(`/books/ledgers/${guid}/statement`, params);
  return (
    <div className="space-y-3">
      {showFilter && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <PeriodFilter value={period} onChange={setPeriod} fyMonth={fyMonth} />
          <ExportMenu path={`/books/ledgers/${guid}/statement`} params={params} />
        </div>
      )}
      <Loaded state={state}>
        {state.data && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Fig label="Opening balance" value={drcr(state.data.opening)} />
              <Fig label="Debits" value={`₹${amount(state.data.total_debit)}`} />
              <Fig label="Credits" value={`₹${amount(state.data.total_credit)}`} />
              <Fig label="Closing balance" value={drcr(state.data.closing)} strong />
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 text-left">Date</th>
                    <th className="px-4 py-2.5 text-left">Particulars</th>
                    <th className="px-4 py-2.5 text-left">Vch type</th>
                    <th className="px-4 py-2.5 text-left">Vch no.</th>
                    <th className="px-4 py-2.5 text-right">Debit</th>
                    <th className="px-4 py-2.5 text-right">Credit</th>
                    <th className="px-4 py-2.5 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100 bg-slate-50/40">
                    <td className="px-4 py-2 text-slate-500">{dateLabel(state.data.from)}</td>
                    <td className="px-4 py-2 font-medium" colSpan={5}>Opening balance</td>
                    <td className="px-4 py-2 text-right tabular-nums">{drcr(state.data.opening)}</td>
                  </tr>
                  {state.data.rows.map((r, i) => (
                    <tr key={`${r.voucher_guid}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40">
                      <td className="whitespace-nowrap px-4 py-2">{dateLabel(r.date)}</td>
                      <td className="px-4 py-2">
                        <Link to={`/vouchers/${r.voucher_guid}`} className="text-blue-700 hover:underline">{r.particulars || "—"}</Link>
                        {r.narration && <div className="max-w-md truncate text-xs text-slate-500">{r.narration}</div>}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{r.voucher_type}</td>
                      <td className="px-4 py-2 text-slate-600">{r.number}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.debit ? amount(r.debit) : ""}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.credit ? amount(r.credit) : ""}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">{drcr(r.balance)}</td>
                    </tr>
                  ))}
                  {state.data.rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No entries in this period.</td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50/70 font-semibold">
                    <td className="px-4 py-2.5" colSpan={4}>Closing balance</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.total_debit)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.total_credit)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{drcr(state.data.closing)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {state.data.truncated && (
              <div className="text-xs text-amber-700">Showing the first 5,000 entries — choose a shorter period to see the rest.</div>
            )}
          </>
        )}
      </Loaded>
    </div>
  );
}

function Fig({ label, value, strong }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-0.5 tabular-nums ${strong ? "text-lg font-semibold" : "font-medium"}`}>{value}</div>
    </div>
  );
}
